<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'

import { CarOfferWidgetType } from '@xcel-next/core/enums'


import { useConfigStore } from '../../stores/config'
import { hashString, ucFirst } from '../../utils/strings'
import Icon from './../shared/Icon.vue'
import Cta from './ParagraphTypeCarOfferWidget/Cta.vue'
import CarImage from './ParagraphTypeCarOfferWidget/CarImage.vue'
import Prices from './ParagraphTypeCarOfferWidget/Prices.vue'
import Presenter from './ParagraphTypeCarOfferWidget/Presenter.vue'

import type { ComposedPageMeta } from '@xcel-next/composer-schema'
import type { Brand } from '@xcel-next/core/enums'
import type { CarOfferWidgetParagraph } from '../../utils/paragraph-schema/paragraphs'

type LinkType = CarOfferWidgetType.buy | CarOfferWidgetType.lease

defineOptions({ name: 'ParagraphTypeCarOfferWidget' })

const props = defineProps <{
  carOfferWidget: CarOfferWidgetParagraph['carOfferWidget']
  brand?: Brand
  pageMeta?: ComposedPageMeta
}>()

const { rsConfig } = storeToRefs(useConfigStore())

const selectedMake = ref('')
const selectedModel = ref('')

const isFind = computed(() => props.carOfferWidget.type === CarOfferWidgetType.find)
const isDuoTeaser = computed(() => props.carOfferWidget.type === CarOfferWidgetType.buyOrLease)
const wrapperTag = computed(() => isDuoTeaser.value ? 'div' : 'a')

const sortedMakes = computed(() => [...(props.carOfferWidget.find?.makes ?? [])].sort((a, b) => a.makeName.localeCompare(b.makeName)))

const availableModels = computed(() => {
  if (!isFind.value) return []
  const make = props.carOfferWidget.find?.makes?.find(m => m.make === selectedMake.value)
  return [...(make?.models ?? [])].sort((a, b) => a.model.localeCompare(b.model))
})

const canSubmit = computed(() => Boolean(selectedMake.value && selectedModel.value))

const utmContentPrefix: Record<CarOfferWidgetType, string> = {
  [CarOfferWidgetType.find]: 'Marke-Modell',
  [CarOfferWidgetType.buy]: 'Barkauf',
  [CarOfferWidgetType.lease]: 'Leasing',
  [CarOfferWidgetType.buyOrLease]: 'Barkauf-und-Leasing'
}

/*
  Leasing: https://www.carwow.de/[make]/[model]/leasing?affiliate=azo&utm_medium=editorial&utm_source=affiliate&utm_campaign=af_de_bauer-azo&utm_content=Marke-Modell-Teaser_[Artikel-Node-ID]
  Barkauf: https://www.carwow.de/[make]/[model]?affiliate=azo&utm_medium=editorial&utm_source=affiliate&utm_campaign=af_de_bauer-azo&utm_content=Marke-Modell-Teaser_[Artikel-Node-ID]
*/
const buildCarwowUrl = (linkType: LinkType) => {
  if (isFind.value && !canSubmit.value) return null

  const carwow = rsConfig.value.carwow
  if (!carwow?.baseUrl) return null

  const path = `/${selectedMake.value}/${selectedModel.value}${linkType === CarOfferWidgetType.lease ? '/leasing' : ''}`

  const params = new URLSearchParams({
    affiliate: carwow?.cta?.affiliate ?? '',
    utm_medium: 'editorial',
    utm_source: 'affiliate',
    utm_campaign: carwow?.cta?.utmCampaign ?? '',
    utm_content: `${props.carOfferWidget.type ? utmContentPrefix[props.carOfferWidget.type] : ''}-Teaser_${props.pageMeta?.nodeId}`
  })

  return `${carwow.baseUrl}${path}?${params.toString()}`
}

const buyUrl = computed(() => buildCarwowUrl(CarOfferWidgetType.buy))
const leaseUrl = computed(() => buildCarwowUrl(CarOfferWidgetType.lease))

const gaCocoGroupType = computed(() => `carOffer${ucFirst(props.carOfferWidget?.type ?? '')}`)

const gaCocoUniqueId = computed(() => {
  const uniqueString = isFind.value
    ? `${props.carOfferWidget.type}-${props.pageMeta?.nodeId}`
    : `${props.carOfferWidget.type}-${selectedMake.value}-${selectedModel.value}`
  return hashString(uniqueString)
})

const singleLinkAttributes = (forceLink?: LinkType) => {
  if (isDuoTeaser.value && !forceLink) return {}
  return {
    href: props.carOfferWidget.type === CarOfferWidgetType.lease || forceLink === CarOfferWidgetType.lease ? leaseUrl.value ?? undefined : buyUrl.value ?? undefined,
    target: '_blank',
    rel: 'sponsored noopener'
  }
}

const openInNewTab = (url: string | null) => {
  if (!url) return
  window.open(url, '_blank', 'noopener')
}

watch(selectedMake, () => {
  if (isFind.value) {
    selectedModel.value = ''
  }
})

onMounted(() => {
  if (isFind.value) return
  selectedMake.value = props.carOfferWidget.car?.make ?? ''
  selectedModel.value = props.carOfferWidget.car?.modelSlug ?? ''
})
</script>

<template>
  <section class="bx-content bx-carwow bx-typo--carwow"
           :class="{
             [`bx-carwow--${carOfferWidget.type?.toLowerCase()}`]: true,
             'bx-carwow--expired': carOfferWidget.expired
           }"
           :data-ga-coco-group-type="gaCocoGroupType"
           :data-ga-coco-unique-id="gaCocoUniqueId">
    <div class="bx-affiliate-info">
      <span class="bx-badge bx-badge--affiliate" />
    </div>

    <div v-if="carOfferWidget.type === CarOfferWidgetType.find"
         class="bx-carwow__wrapper">
      <Presenter />
      <div class="bx-carwow__headline">
        {{ carOfferWidget.headline }}
      </div>
      <div class="bx-carwow__pro-image-wrapper">
        <CarImage :type="carOfferWidget.type"
                  :brand="brand"
                  :image="carOfferWidget.image" />
        <div class="bx-carwow__pros">
          <div>{{ carOfferWidget.pros?.headline }}</div>
          <ul v-if="carOfferWidget.pros?.bulletPoints?.length">
            <li v-for="pro in carOfferWidget.pros.bulletPoints"
                :key="pro">
              <Icon name="seal-check" />
              {{ pro }}
            </li>
          </ul>
        </div>
      </div>
      <form class="bx-form bx-typo--form bx-form--pur bx-typo--form-contact bx-form--carwow"
            @submit.prevent>
        <div class="bx-form__select-wrapper">
          <label class="bx-form__label"
                 for="make">
            Marke
          </label>
          <select id="make"
                  v-model="selectedMake"
                  class="bx-form__select">
            <option disabled
                    value="">
              Bitte auswählen
            </option>
            <option v-for="make in sortedMakes"
                    :key="make.make"
                    :value="make.make">
              {{ make.makeName }}
            </option>
          </select>
          <Icon name="arrow_toggle" />
        </div>
        <div class="bx-form__select-wrapper">
          <label class="bx-form__label"
                 for="modell">
            Modell
          </label>
          <select id="modell"
                  v-model="selectedModel"
                  :disabled="!selectedMake"
                  class="bx-form__select">
            <option disabled
                    value="">
              Bitte auswählen
            </option>
            <option v-for="model in availableModels"
                    :key="model.modelSlug"
                    :value="model.modelSlug">
              {{ model.model }}
            </option>
          </select>
          <Icon name="arrow_toggle" />
        </div>
        <button class="bx-form__button"
                type="button"
                :disabled="!canSubmit"
                :data-href="buyUrl"
                @click="openInNewTab(buyUrl)">
          Barkauf
        </button>
        <button class="bx-form__button"
                type="button"
                :disabled="!canSubmit"
                :data-href="leaseUrl"
                @click="openInNewTab(leaseUrl)">
          Leasing
        </button>
      </form>
    </div>

    <component :is="wrapperTag"
               v-else
               v-bind="singleLinkAttributes()"
               class="bx-carwow__wrapper">
      <div class="bx-carwow__header">
        <span :class="{ 'bx-carwow__badge--empty': !carOfferWidget.badge }">{{ carOfferWidget.badge }}</span>
        <span>{{ carOfferWidget.headline }}</span>
      </div>
      <div class="bx-carwow__subheadline">
        {{ carOfferWidget.subheadline }}
      </div>
      <div class="bx-carwow__deal">
        <CarImage :type="carOfferWidget.type"
                  :brand="brand"
                  :image="carOfferWidget.image" />
        <div v-if="isDuoTeaser"
             class="bx-carwow__duo">
          <a v-bind="singleLinkAttributes(CarOfferWidgetType.buy)">
            <span class="bx-carwow__duo-topic">Barkauf</span>
            <Prices :type="CarOfferWidgetType.buy"
                    :expired="!carOfferWidget.buy?.price"
                    :buy="carOfferWidget.buy" />
            <Cta :show-logo="false" />
          </a>
          <a v-bind="singleLinkAttributes(CarOfferWidgetType.lease)">
            <span class="bx-carwow__duo-topic">Leasing</span>
            <Prices :type="CarOfferWidgetType.lease"
                    :expired="!carOfferWidget.lease?.rate"
                    :lease="carOfferWidget.lease" />
            <Cta :show-logo="false" />
          </a>
        </div>
        <div v-else>
          <Prices :type="carOfferWidget.type"
                  :expired="carOfferWidget.expired"
                  :buy="carOfferWidget.buy"
                  :lease="carOfferWidget.lease" />
          <Cta />
        </div>
        <Presenter v-if="isDuoTeaser" />
      </div>
    </component>
    <div v-if="carOfferWidget.disclaimer?.show"
         class="bx-carwow__disclaimer">
      {{ carOfferWidget.disclaimer.text }}
    </div>
  </section>
</template>
